import React, { Fragment, useEffect, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
	Stack,
	Box,
	Typography,
	CircularProgress,
	Paper,
	Button,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TableSortLabel
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import NumericStepper from 'components/NumericStepper';
import PromoIcon from 'components/PromoIcon';
import utils from 'components/utils';

const KitAccordionDetails = props => {
	const { kit, openDetail } = props;
	const theme = useTheme();
	const translate = utils.useI18N();
	const loadingDetail = useStoreState(state => state.kitUsers.loadingDetail);
	const getDetail = useStoreActions(dispatch => dispatch.kitUsers.getDetail);
	const kitDetailItems = useStoreState(state => state.kitUsers.kitDetailItems);
	const setDetailEan = useStoreActions(dispatch => dispatch.kitUsers.setDetailEan);
	const book = useStoreActions(dispatch => dispatch.kitUsers.book);
	const order = useStoreState(state => state.kitUsers.orderDirection);
	const orderBy = useStoreState(state => state.kitUsers.orderBy);
	const setOrder = useStoreActions(dispatch => dispatch.kitUsers.setOrder);
	const sortedRows = utils.stableSort(kitDetailItems, order, orderBy);
	const orderable = kit.available - kit.committed;
	const [qta, setQta] = useState(1);
	const headCells = [
		{ id: 'ean', label: 'catalog.tableHeaders.ean' },
		{ id: 'author', label: 'catalog.tableHeaders.author' },
		{ id: 'title', label: 'catalog.tableHeaders.title' },
		{ id: 'serie', label: 'catalog.tableHeaders.serie' },
		{ id: 'publisher', label: 'catalog.tableHeaders.publisher' },
		{ id: 'fullPrice', label: 'catalog.tableHeaders.price' },
		{ id: 'depot', label: 'catalog.tableHeaders.depot' },
		{ id: 'requestQty', label: 'catalog.tableHeaders.qta', sx: { textAlign: 'center' } }
	];

	const setOrderNumber = value => {
		setQta(value);
	};

	const handleOrder = event => {
		book({ kitId: kit.id, qta: qta });
	};

	useEffect(() => {
		getDetail(kit.id);
	}, []);

	return (
		<Fragment>
			<Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={0}>
				{loadingDetail && (
					<Stack justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
						<CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
					</Stack>
				)}
				{!loadingDetail && (
					<Stack spacing={1} sx={{ width: '100%' }}>
						<Stack
							direction={{ xs: 'column', md: 'row', sm: 'row' }}
							justifyContent={{ xs: 'flex-start', md: 'space-between', sm: 'space-between' }}
							alignItems={{ xs: 'flex-start', md: 'center', sm: 'center' }}
							spacing={{ xs: 2, md: 0, sm: 0 }}
							sx={{ width: '100%', marginBottom: '10px !important' }}
						>
							{kit.promoDesc && kit.promoDesc !== '' && (
								<Stack
									direction="row"
									justifyContent="flex-start"
									alignItems="center"
									spacing={0.5}
									sx={{ width: { xs: '100%', md: '50%', sm: '50%' } }}
								>
									<PromoIcon />
									<Typography variant="text" sx={{ color: theme.palette.custom.green }}>
										{kit.promoDesc}
									</Typography>
								</Stack>
							)}
							<Stack
								direction="row"
								justifyContent="flex-end"
								alignItems="center"
								spacing={0.5}
								sx={{
									width: {
										xs: '100%',
										md: kit.promoDesc && kit.promoDesc !== '' ? '50%' : '100%',
										sm: kit.promoDesc && kit.promoDesc !== '' ? '50%' : '100%'
									}
								}}
							>
								<Box sx={{ width: '90px' }}>
									<NumericStepper
										removeDot
										value={qta}
										max={orderable}
										precision={0}
										min={1}
										strict
										onChange={numberAsValue => setOrderNumber(numberAsValue)}
									/>
								</Box>
								<Button variant="dark" endIcon={<AddShoppingCartIcon />} onClick={handleOrder}>
									{translate('kit.addToCart')}
								</Button>
							</Stack>
						</Stack>
						<TableContainer component={Paper} sx={{ width: '100%', maxWidth: '1300px' }}>
							<Table>
								<TableHead>
									<TableRow>
										{headCells.map(headCell => (
											<TableCell
												key={headCell.id}
												align="left"
												sortDirection={orderBy === headCell.id ? order : false}
												style={headCell.sx}
												sx={{
													background: theme.palette.custom.greytable,
													fontWeight: '600',
													padding: '0 0 0 4px'
												}}
											>
												<TableSortLabel
													active={orderBy === headCell.id}
													direction={orderBy === headCell.id ? order : 'asc'}
													onClick={e => setOrder(headCell.id)}
													sx={{ lineHeight: '18px' }}
												>
													{headCell.label && translate(headCell.label)}
												</TableSortLabel>
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{sortedRows.map((row, index) => (
										<TableRow key={`row_${index}`}>
											<TableCell sx={{ padding: '4px 0 4px 4px' }}>
												<Typography variant="textTable">{row.ean}</Typography>
											</TableCell>
											<TableCell sx={{ padding: '4px 0 4px 4px', textTransform: 'capitalize' }}>
												<Typography variant="textTable">{row.author}</Typography>
											</TableCell>
											<TableCell sx={{ padding: '4px 0 4px 4px' }}>
												<Link
													to="#"
													onClick={_ => {
														setDetailEan(row.ean);
													}}
													style={{ textDecoration: 'none' }}
												>
													<Typography
														variant="textTable"
														sx={{ color: theme.palette.primary.main }}
													>
														{row.title}
													</Typography>
												</Link>
											</TableCell>
											<TableCell sx={{ padding: '4px 0 4px 4px' }}>
												<Typography variant="textTable">{row.serie}</Typography>
											</TableCell>
											<TableCell sx={{ padding: '4px 0 4px 4px' }}>
												<Typography variant="textTable">{row.publisher}</Typography>
											</TableCell>
											<TableCell sx={{ padding: '4px 0 4px 4px' }}>
												<Typography variant="textTable">
													{utils.formatPrice(row.fullPrice)}
												</Typography>
											</TableCell>
											<TableCell sx={{ padding: '4px 0 4px 4px' }}>
												<Typography variant="textTable">{row.depotDesc}</Typography>
											</TableCell>
											<TableCell sx={{ padding: '4px 0 4px 4px', textAlign: 'center' }}>
												<Typography variant="textTable">{row.quantity}</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Stack>
				)}
			</Stack>
		</Fragment>
	);
};

export default KitAccordionDetails;
